import { type Node } from '@xyflow/react';
import { useCallback } from 'react';

// Helper to manage node presence in localStorage
const manageNodePresence = (nodeId: string | null) => {
  const currentNodeId = localStorage.getItem('currentNodeId');
  if (currentNodeId !== nodeId) {
    localStorage.setItem('lastNodeId', currentNodeId || '');
    if (nodeId) {
      localStorage.setItem('currentNodeId', nodeId);
    } else {
      localStorage.removeItem('currentNodeId');
    }
  }
  return { currentNodeId, lastNodeId: localStorage.getItem('lastNodeId') || null };
};

export function useNodeOperations(
  nodes: Node[],
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
  setNodeSynced: (node: Node) => void,
  setEdges: React.Dispatch<React.SetStateAction<any[]>>,
  getNode: (id: string) => Node | undefined,
  user: { email: string },
  startDrag?: (nodeId: string) => void,
  stopDrag?: (nodeId: string) => void,
  forceSyncNode?: (node: Node) => void,
  selectNode?: (nodeId: string | null) => void,
  onNodesChange?: (changes: any[]) => void
) {
  const addNode = useCallback(
    (newNode: Node) => {
      setNodes((nds: Node[]): Node[] => [...nds, newNode]);
    },
    [setNodes]
  );

  const updateNodePosition = useCallback(
    (nodeId: string, position: { x: number; y: number }, skipSync = false) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            const updatedNode = { ...node, position };
            if (!skipSync) {
              setNodeSynced(updatedNode);
            }
            return updatedNode;
          }
          return node;
        })
      );
    },
    [setNodes, setNodeSynced]
  );
  const removeNode = useCallback(
    (nodeId: string) => {
      if (onNodesChange) {
        // Use ReactFlow's proper change mechanism to trigger YJS sync
        const removeChange = {
          type: 'remove' as const,
          id: nodeId,
        };
        onNodesChange([removeChange]);
      } else {
        // Fallback to direct state manipulation if onNodesChange not available
        setNodes((nds) => nds.filter((node) => node.id !== nodeId));
        setEdges((eds) =>
          eds.filter((edge) => {
            return (
              edge?.source &&
              edge.target &&
              edge.source !== nodeId &&
              edge.target !== nodeId
            );
          })
        );
      }
    },
    [setNodes, setEdges, onNodesChange]
  );

  const duplicateNode = useCallback(
    (id: string, callback?: (newNodeId: string) => void) => {
      const node = getNode(id);
      if (node) {
        const position = {
          x: node.position.x + 50,
          y: node.position.y + 50,
        };

        const data = { ...node.data };
        if (
          (node.type === 'nodechat' || node.type === 'nodeintellica') &&
          Array.isArray(data.messages)
        ) {
          data.messages = data.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date().toISOString(),
          }));
        }

        const originalLabel = data.label || 'Untitled';
        const now = new Date();
        const dateStr = now
          .toLocaleString('en-GB', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
          .replace(/\//g, '')
          .replace(',', '-')
          .replace(/:/g, '');
        const randomNum = Math.floor(Math.random() * 900 + 100);
        data.label = `Copy of: ${originalLabel} ${dateStr} ${randomNum}`;

        // Reset actions total and add status message for duplicated node
        data.actionsTotal = 0;
        data.statusMessage = "This is the new duplicated node. Duplication completed.";

        const newNodeId = `${node.originalId || node.id}-copy-${randomNum}`;
        const newNode: Node = {
          ...node,
          // @ts-ignore
          id: newNodeId,
          // @ts-ignore
          originalId: node.originalId || node.id,
          position,
          selected: false,
          data,
        };

        // Add status message to original node
        const originalNode = { ...node };
        originalNode.data = {
          ...originalNode.data,
          statusMessage: "Duplication completed."
        };

        // Update both nodes
        setNodeSynced(originalNode);
        addNode(newNode);

        if (callback) callback(newNodeId);
      }
    },
    [getNode, addNode, setNodeSynced]
  );

  const onNodeDragStart = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (startDrag) {
        startDrag(node.id);
      }
      updateNodePosition(node.id, node.position, true);
    },
    [startDrag, updateNodePosition]
  );

  const onNodeDragStop = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (stopDrag) {
        stopDrag(node.id);
      }
      if (forceSyncNode) {
        forceSyncNode(node);
      } else {
        updateNodePosition(node.id, node.position);
      }
    },
    [stopDrag, forceSyncNode, updateNodePosition]
  );

  const handleSectionStateChange = useCallback(
    (
      nodeId: string,
      sectionId: string,
      state:
        | 'Draft'
        | 'Detailed'
        | 'Styled'
        | 'GrowList'
        | 'ShrinkList'
        | 'CreateSubparagraph'
        | 'ExpandDetail'
        | 'Explain'
        | 'ChangeTone'
        | 'DivideIntoSections'
        | string
    ) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            const section = node.data.selectedSection;
            if (!section) {
              console.error(
                `No selectedSection found in node ${nodeId} data, cannot process state change`
              );
              return node;
            }

            const isExplanationCommand = state === 'Explain';
            const commandType = isExplanationCommand ? 'explanation' : 'modification';

            const sectionCommand = {
              command: state,
              sectionId: sectionId,
              sectionTitle: section.title,
              content: section.content,
              commandType,
              'sysagent-cmd': isExplanationCommand
                ? 'SectionExplanation-Process-Started'
                : 'SectionCommand-Process-Started',
              'sysagent-response': '',
            };

            const updatedNode = {
              ...node,
              data: {
                ...node.data,
                sectionCommand,
                sectionStates: {
                  ...(node.data.sectionStates || {}),
                  [sectionId]: state,
                },
                sectionExplanations: node.data.sectionExplanations || {},
                sectionExplanationStates: node.data.sectionExplanationStates || {},
              },
            };

            setNodeSynced(updatedNode);
            return updatedNode;
          }
          return node;
        })
      );
    },
    [setNodes, setNodeSynced]
  );

  const findPresenceNode = useCallback(() => {
    return nodes.find((node) => node.type === 'nodepresence');
  }, [nodes]);

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (!user.email) {
        console.error('onNodeClick: user.email is undefined, cannot update presence');
        return;
      }

      if (node.type === 'nodepresence') {
        const currentNodeId = localStorage.getItem('currentNodeId');
        localStorage.removeItem('currentNodeId');
        if (currentNodeId) {
          localStorage.setItem('lastNodeId', currentNodeId);
        }

        if (currentNodeId) {
          const currentNode = getNode(currentNodeId);
          if (currentNode && currentNode.data?.activeUsers?.[user.email]) {
            const updatedNode = JSON.parse(JSON.stringify(currentNode));
            if (updatedNode.data?.activeUsers) {
              delete updatedNode.data.activeUsers[user.email];
            }
            setNodeSynced(updatedNode);
            setNodes((nds) =>
              nds.map((n) => {
                if (n.id === currentNodeId && n.data?.activeUsers?.[user.email]) {
                  const activeUsers = { ...n.data.activeUsers };
                  delete activeUsers[user.email];
                  return { ...n, data: { ...n.data, activeUsers } };
                }
                return n;
              })
            );
          }
        }

        const presenceNode = findPresenceNode();
        if (presenceNode) {
          const updatedPresenceNode = JSON.parse(JSON.stringify(presenceNode));
          if (!updatedPresenceNode.data) updatedPresenceNode.data = {};
          if (!updatedPresenceNode.data.activeUsers) updatedPresenceNode.data.activeUsers = {};
          updatedPresenceNode.data.activeUsers[user.email] = {
            email: user.email,
            currentNodeId: null,
            lastSeen: Date.now(),
            status: 'online',
          };
          setNodeSynced(updatedPresenceNode);
        }
        return;
      }

      const { currentNodeId, lastNodeId } = manageNodePresence(node.id);
      if (currentNodeId === node.id) return;

      const clickedNode = getNode(node.id);
      if (clickedNode) {
        const updatedClickedNode = JSON.parse(JSON.stringify(clickedNode));
        if (!updatedClickedNode.data) updatedClickedNode.data = {};
        if (!updatedClickedNode.data.activeUsers) updatedClickedNode.data.activeUsers = {};
        updatedClickedNode.data.activeUsers[user.email] = {
          email: user.email,
          currentNodeId: node.id,
          lastSeen: Date.now(),
          status: 'viewing',
        };
        setNodeSynced(updatedClickedNode);
      }

      if (lastNodeId) {
        const lastNode = getNode(lastNodeId);
        if (lastNode && lastNode.data?.activeUsers?.[user.email]) {
          const updatedLastNode = JSON.parse(JSON.stringify(lastNode));
          if (updatedLastNode.data?.activeUsers) {
            delete updatedLastNode.data.activeUsers[user.email];
          }
          setNodeSynced(updatedLastNode);
        }
      }

      const presenceNode = findPresenceNode();
      if (presenceNode) {
        const updatedPresenceNode = JSON.parse(JSON.stringify(presenceNode));
        if (!updatedPresenceNode.data) updatedPresenceNode.data = {};
        if (!updatedPresenceNode.data.activeUsers) updatedPresenceNode.data.activeUsers = {};
        updatedPresenceNode.data.activeUsers[user.email] = {
          email: user.email,
          currentNodeId: node.id,
          lastSeen: Date.now(),
          status: 'viewing',
        };
        setNodeSynced(updatedPresenceNode);
      }

      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === node.id) {
            const activeUsers = { ...(n.data?.activeUsers || {}) };
            activeUsers[user.email] = {
              email: user.email,
              currentNodeId: node.id,
              lastSeen: Date.now(),
              status: 'viewing',
            };
            return {
              ...n,
              data: { ...n.data, activeUsers },
            };
          } else if (n.type === 'nodepresence') {
            const activeUsers = { ...(n.data?.activeUsers || {}) };
            activeUsers[user.email] = {
              email: user.email,
              currentNodeId: node.id,
              lastSeen: Date.now(),
              status: 'viewing',
            };
            return {
              ...n,
              data: { ...n.data, activeUsers },
            };
          } else if (lastNodeId && n.id === lastNodeId) {
            const activeUsers = { ...(n.data?.activeUsers || {}) };
            delete activeUsers[user.email];
            return {
              ...n,
              data: { ...n.data, activeUsers },
            };
          }
          return n;
        })
      );

      if (selectNode) {
        selectNode(node.id);
      }
    },
    [setNodes, setNodeSynced, getNode, user.email, findPresenceNode, selectNode]
  );

  const onPaneClick = useCallback(
    (event: React.MouseEvent) => {
      if (!user.email) {
        console.error('onPaneClick: user.email is undefined, cannot clear presence');
        return;
      }

      const currentNodeId = localStorage.getItem('currentNodeId');
      const lastNodeId = localStorage.getItem('lastNodeId');

      localStorage.removeItem('currentNodeId');
      localStorage.removeItem('lastNodeId');

      if (currentNodeId) {
        const currentNode = getNode(currentNodeId);
        if (currentNode && currentNode.data?.activeUsers?.[user.email]) {
          const updatedCurrentNode = JSON.parse(JSON.stringify(currentNode));
          delete updatedCurrentNode.data.activeUsers[user.email];
          setNodeSynced(updatedCurrentNode);
        }
      }

      if (lastNodeId && lastNodeId !== currentNodeId) {
        const lastNode = getNode(lastNodeId);
        if (lastNode && lastNode.data?.activeUsers?.[user.email]) {
          const updatedLastNode = JSON.parse(JSON.stringify(lastNode));
          delete updatedLastNode.data.activeUsers[user.email];
          setNodeSynced(updatedLastNode);
        }
      }

      const presenceNode = findPresenceNode();
      if (presenceNode && presenceNode.data?.activeUsers?.[user.email]) {
        const updatedPresenceNode = JSON.parse(JSON.stringify(presenceNode));
        updatedPresenceNode.data.activeUsers[user.email] = {
          ...updatedPresenceNode.data.activeUsers[user.email],
          currentNodeId: null,
          lastSeen: Date.now(),
          status: 'online',
        };
        setNodeSynced(updatedPresenceNode);
      }

      setNodes((currentNodes) =>
        currentNodes.map((n) => {
          if (
            (n.id === currentNodeId || n.id === lastNodeId) &&
            n.data?.activeUsers?.[user.email]
          ) {
            const activeUsers = { ...n.data.activeUsers };
            delete activeUsers[user.email];
            return { ...n, data: { ...n.data, activeUsers } };
          } else if (n.type === 'nodepresence' && n.data?.activeUsers?.[user.email]) {
            const activeUsers = { ...n.data.activeUsers };
            activeUsers[user.email] = {
              ...activeUsers[user.email],
              currentNodeId: null,
              lastSeen: Date.now(),
              status: 'online',
            };
            return { ...n, data: { ...n.data, activeUsers } };
          }
          return n;
        })
      );

      if (selectNode) {
        selectNode(null);
      }
    },
    [user.email, getNode, setNodeSynced, setNodes, findPresenceNode, selectNode]
  );

  return {
    addNode,
    updateNodePosition,
    removeNode,
    duplicateNode,
    onNodeDragStart,
    onNodeDragStop,
    onNodeClick,
    onPaneClick,
    handleSectionStateChange,
    findPresenceNode,
  };
}